import React, {useEffect, useMemo, useRef, CSSProperties} from 'react';
import { useSelector } from 'react-redux';
import Hls from 'hls.js';
import {GreenroomState, GreenroomState as GS} from '../stores/types/types';
import { STREAMING_TYPES, VID_TYPES } from '../types/constants';
import { isVideo } from './utils';

type PreloadTarget = {
	url: string;
	isStreaming: boolean;
};

const hiddenPreloadStyles: CSSProperties = {
	position: 'absolute',
	width: 1,
	height: 1,
	overflow: 'hidden',
	opacity: 0,
	pointerEvents: 'none',
	left: -9999,
	top: -9999,
};

const isStreamingUrl = (url: string) => {
const extension = url.split(/[#?]/)[0].split('.').pop()?.toLowerCase() ?? '';
	return STREAMING_TYPES.includes(extension);
};

const destroyHlsInstance = (hls: Hls) => {
	try {
		hls.stopLoad();
		hls.destroy();
	} catch (err) {
		// Ignore SourceBuffer removed errors during cleanup
		if (!(err instanceof DOMException && err.message.includes('SourceBuffer'))) {
			console.error('Error destroying HLS preload instance:', err);
		}
	}
};

function HiddenNativeVideoPreloader({ src }: { src: string }) {
	const videoRef = useRef<HTMLVideoElement>(null);

	useEffect(() => {
		const video = videoRef.current;
		if (!video) return undefined;

		video.load();

		return () => {
			video.pause();
			video.removeAttribute('src');
			video.load();
		};
	}, [src]);

	return (
		<video
			ref={videoRef}
			src={src}
			preload="auto"
			muted={true}
			playsInline={true}
			crossOrigin="anonymous"
			tabIndex={-1}
		/>
	);
}

function HiddenHlsVideoPreloader({ src }: { src: string }) {
	const videoRef = useRef<HTMLVideoElement>(null);

	useEffect(() => {
		const video = videoRef.current;
		if (!video) return undefined;

		if (!Hls.isSupported()) {
			video.src = src;
			video.load();

			return () => {
				video.pause();
				video.removeAttribute('src');
				video.load();
			};
		}

		const hls = new Hls();
		let isDestroyed = false;

		const cleanup = () => {
			if (isDestroyed) return;
			isDestroyed = true;
			destroyHlsInstance(hls);
		};

		hls.loadSource(src);
		hls.attachMedia(video);
		hls.on(Hls.Events.ERROR, (_event, data) => {
			if (data.fatal) {
				cleanup();
			}
		});

		return () => {
			cleanup();
			video.pause();
			video.removeAttribute('src');
			video.load();
		};
	}, [src]);

	return (
		<video
			ref={videoRef}
			preload="auto"
			muted={true}
			playsInline={true}
			crossOrigin="anonymous"
			tabIndex={-1}
		/>
	);
}

/**
 * Created to preload the next video in the show, so that it is ready to play when the current shot ends.
 * We were running into an issue where bullman was delaying playing a video when switching shots, we are
 * hoping this will fix that issue.
 */
export default function PreloadVideo() {
	const currentShotId = useSelector((s: GS) => s.ShotsReducer.currentShotId);
	const shots = useSelector((state: GreenroomState) => state.ShotsReducer.shots);
	const media = useSelector((state: GreenroomState) => state.MediaReducer.media);
	const graphics = useSelector((state: GreenroomState) => state.GraphicsReducer.graphics);

	const currentShot = useMemo(() => {
		if (currentShotId) {
			return shots.find(s => s.shot_id === currentShotId);
		}
		return shots?.[0];
	}, [currentShotId, shots]);

	const nextShot = useMemo(() => {
		if (!currentShot) return;

		const currentShotIndex = shots.findIndex(s => s.shot_id === currentShot?.shot_id);
		if (currentShotIndex >= 0 && currentShotIndex < shots.length - 1) {
			return shots[currentShotIndex + 1];
		} else if (currentShotIndex < 0) {
			return shots[0];
		}
		return shots?.[currentShotIndex];

	}, [currentShot, shots]);

	const mediaTarget = useMemo((): PreloadTarget | null => {
		if (!currentShot || !nextShot || currentShot.shot_id === nextShot.shot_id) return null;

		const shotMedia = nextShot.shot_data?.shotFeeds.filter(f => f.feedType === 'media');
		const firstShotFeedWithVideo = shotMedia?.find((sf) => media.find(
			m => m.id === sf.media && VID_TYPES.includes(m.type),
		));

		if (!firstShotFeedWithVideo) return null;

		const mediaItem = media.find(m => m.id === firstShotFeedWithVideo.media);
		if (!mediaItem?.images) return null;

		return {
			url: mediaItem.images,
			isStreaming: isStreamingUrl(mediaItem.images),
		};
	}, [nextShot, currentShot, media]);

	const overlayTargets = useMemo((): PreloadTarget[] => {
		if (!currentShot || !nextShot || currentShot.shot_id === nextShot.shot_id) return [];

		const seen = new Set<string>();
		const shotGraphicIds = nextShot.shot_data?.shotGraphics ?? [];

		return shotGraphicIds.flatMap((graphicId: number) => {
			const graphic = graphics.find(g => g.id === graphicId);
			if (!graphic?.images || !isVideo(graphic.images) || seen.has(graphic.images)) {
				return [];
			}

			seen.add(graphic.images);
			return [{
				url: graphic.images,
				isStreaming: isStreamingUrl(graphic.images),
			}];
		});
	}, [nextShot, currentShot, graphics]);

	const preloadTargets = useMemo(() => {
		const targets: PreloadTarget[] = [];
		const seen = new Set<string>();

		const addTarget = (target: PreloadTarget | null) => {
			if (!target || seen.has(target.url)) return;
			seen.add(target.url);
			targets.push(target);
		};

		addTarget(mediaTarget);
		overlayTargets.forEach(addTarget);

		return targets;
	}, [mediaTarget, overlayTargets]);

	if (!preloadTargets.length) {
		return (<></>);
	}

	return (
		<div aria-hidden="true" style={hiddenPreloadStyles}>
			{preloadTargets.map((target) => (
				target.isStreaming
					? <HiddenHlsVideoPreloader key={target.url} src={target.url} />
					: <HiddenNativeVideoPreloader key={target.url} src={target.url} />
			))}
		</div>
	);
}
